import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { cn } from '@/lib/cn';
import type { PondTab } from './tabs';

export function Section({
  title,
  action,
  children,
  className,
  labelledBy,
}: {
  title: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  labelledBy: string;
}) {
  return (
    <section
      aria-labelledby={labelledBy}
      className={cn('flex min-w-0 flex-col gap-3 rounded-xl border border-gray-200/80 bg-white px-5 py-4 shadow-sm', className)}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id={labelledBy} className="text-base font-semibold text-gray-900">
          {title}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export function Kicker({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <span className={cn('text-[11px] font-semibold uppercase tracking-wider text-gray-500', className)}>
      {children}
    </span>
  );
}

export type ChipTone = 'ok' | 'warn' | 'crit' | 'neutral' | 'prep' | 'stock';

const chipTone: Record<ChipTone, string> = {
  ok: 'bg-emerald-100 text-emerald-800',
  warn: 'bg-amber-100 text-amber-800',
  crit: 'bg-red-100 text-red-800',
  neutral: 'bg-gray-100 text-gray-600',
  prep: 'bg-blue-100 text-blue-800',
  stock: 'bg-amber-100 text-amber-900',
};

export function Chip({ tone, children, className }: { tone: ChipTone; children: ReactNode; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold tabular-nums',
        chipTone[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

export function TabLink({ pondId, tab, children }: { pondId: string; tab: PondTab; children: ReactNode }) {
  return (
    <Link
      to="/ponds/$pondId"
      params={{ pondId }}
      search={(prev) => ({ ...prev, tab })}
      className="text-sm font-semibold text-primary-600 hover:text-primary-700"
    >
      {children}
    </Link>
  );
}

export function EmptyNote({ children }: { children: ReactNode }) {
  return <p className="text-sm text-gray-500">{children}</p>;
}
